import type { GetStaticPaths } from "next"

import { isSlug, trimText } from "@bounce/util"

import { getCity, getTopCities } from "../../../../libs/bounce/cities"
import { createClient } from "../../../../libs/prismic/createClient"
import { filterCurrentArticle } from "../../../../libs/prismic/event/helpers"
import { getAllGuidesForCityGroupedByType, getGuide } from "../../../../libs/prismic/guide"
import type { ArticleScreenProps } from "../../../../screens/Generic/ArticleScreen/ArticleScreen"
import ArticleScreen from "../../../../screens/Generic/ArticleScreen/ArticleScreen"
import { enhanceStaticProps, notFound } from "../../../../utils/getStaticProps"
import { loadTranslations } from "../../../../utils/i18n/server"

export const getStaticPaths: GetStaticPaths<{ city: string; poi_article: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  }
}

export const getStaticProps = enhanceStaticProps<ArticleScreenProps, { city: string; poi_article: string }>(
  async ({ params, previewData, localeData: { locale, language } }) => {
    const articleUID = params?.poi_article
    const cityUID = params?.city

    if (!isSlug(articleUID) || !isSlug(cityUID)) {
      return notFound()
    }

    const prismicClient = createClient({
      previewData,
    })
    const [translations, article, city] = await Promise.all([
      loadTranslations({ language, namespaces: ["marketing-generic"] }),
      getGuide({ prismicClient, uid: articleUID, params: { lang: locale } }),
      getCity({ prismicClient, citySlug: cityUID, language, locale, onlyActive: false }),
    ])

    if (!article) {
      return {
        redirect: {
          destination: !city ? "/guides" : `/guides/${cityUID}`,
          permanent: false,
        },
      }
    }

    const [guides, topCities] = await Promise.all([
      getAllGuidesForCityGroupedByType({
        prismicClient,
        citySlug: cityUID,
        params: { lang: locale, pageSize: 3 },
      }),
      getTopCities({ prismicClient, language, kind: "luggage" }),
    ])

    const guidesWithoutCurrent = filterCurrentArticle(articleUID, guides.guide)

    // Meta
    article.metaTitle = article.metaTitle || `${article.title} - Bounce`
    article.metaDescription = article.metaDescription || trimText(article.excerpt, 170)

    return {
      props: {
        topCities,
        article,
        relatedArticles: guidesWithoutCurrent,
        city,
        type: "guide",
        languages: [language],
        ...translations,
      },
      revalidate: 60 * 60 * 24 * 5, // 5 hours
    }
  },
)

export default ArticleScreen
